import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Search, Loader2, Plus, Minus, Gift, Trophy, RotateCcw, Trash2, Pencil, UserPlus } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoyaltySection = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const { getAuthHeaders } = useAuth();

  // Dialogs
  const [redeemClient, setRedeemClient] = useState(null);
  const [resetClient, setResetClient] = useState(null);
  const [deleteClient, setDeleteClient] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [editPoints, setEditPoints] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [createLoading, setCreateLoading] = useState(false);

  // Confirm +/- dialogs
  const [addPointClient, setAddPointClient] = useState(null);
  const [removePointClient, setRemovePointClient] = useState(null);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
      setClients(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPoint = async () => {
    if (!addPointClient) return;
    setActionLoading(addPointClient.id + '-add');
    try {
      const res = await axios.post(`${API}/admin/clients/${addPointClient.id}/loyalty/add`, {}, { headers: getAuthHeaders() });
      setClients(prev => prev.map(c => c.id === addPointClient.id ? { ...c, loyalty_points: res.data.loyalty_points } : c));
      toast.success(`+1 punto per ${addPointClient.first_name}!`);
      setAddPointClient(null);
    } catch (error) {
      toast.error('Errore');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemovePoint = async () => {
    if (!removePointClient) return;
    setActionLoading(removePointClient.id + '-remove');
    try {
      const res = await axios.post(`${API}/admin/clients/${removePointClient.id}/loyalty/remove`, {}, { headers: getAuthHeaders() });
      setClients(prev => prev.map(c => c.id === removePointClient.id ? { ...c, loyalty_points: res.data.loyalty_points } : c));
      toast.success(`-1 punto per ${removePointClient.first_name}`);
      setRemovePointClient(null);
    } catch (error) {
      toast.error('Errore');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRedeem = async () => {
    if (!redeemClient) return;
    setActionLoading(redeemClient.id + '-redeem');
    try {
      await axios.post(`${API}/admin/clients/${redeemClient.id}/loyalty/redeem`, {}, { headers: getAuthHeaders() });
      setClients(prev => prev.map(c => c.id === redeemClient.id ? { ...c, loyalty_points: 0 } : c));
      toast.success('Trattamento riscattato! Punti azzerati.');
      setRedeemClient(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReset = async () => {
    if (!resetClient) return;
    try {
      await axios.put(`${API}/clients/${resetClient.id}/loyalty?points=0`, {}, { headers: getAuthHeaders() });
      setClients(prev => prev.map(c => c.id === resetClient.id ? { ...c, loyalty_points: 0 } : c));
      toast.success('Punti azzerati');
      setResetClient(null);
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDelete = async () => {
    if (!deleteClient) return;
    try {
      await axios.delete(`${API}/admin/clients/${deleteClient.id}`, { headers: getAuthHeaders() });
      setClients(prev => prev.filter(c => c.id !== deleteClient.id));
      toast.success(`${deleteClient.first_name} ${deleteClient.last_name} eliminato`);
      setDeleteClient(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleEditPoints = async () => {
    if (!editClient) return;
    const pts = Math.max(0, Math.min(99, parseInt(editPoints) || 0));
    try {
      await axios.put(`${API}/clients/${editClient.id}/loyalty?points=${pts}`, {}, { headers: getAuthHeaders() });
      setClients(prev => prev.map(c => c.id === editClient.id ? { ...c, loyalty_points: pts } : c));
      toast.success(`Punti aggiornati a ${pts}`);
      setEditClient(null);
    } catch (error) {
      toast.error('Errore aggiornamento punti');
    }
  };

  const handleCreate = async () => {
    if (!createForm.first_name || !createForm.last_name || !createForm.email || !createForm.phone || !createForm.password) {
      toast.error('Compila tutti i campi');
      return;
    }
    setCreateLoading(true);
    try {
      await axios.post(`${API}/admin/clients/create`, createForm, { headers: getAuthHeaders() });
      toast.success('Cliente creato!');
      setCreateOpen(false);
      setCreateForm({ first_name: '', last_name: '', email: '', phone: '', password: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione');
    } finally {
      setCreateLoading(false);
    }
  };

  const filtered = clients.filter(c => {
    const term = searchTerm.toLowerCase();
    return `${c.first_name} ${c.last_name}`.toLowerCase().includes(term) ||
           c.email?.toLowerCase().includes(term) ||
           c.phone?.includes(term);
  });

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="loyalty-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Trophy className="h-6 w-6 text-accent" /> Carta Fedeltà
        </h2>
        <Button onClick={() => setCreateOpen(true)} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="loyalty-create-btn">
          <UserPlus className="h-4 w-4 mr-2" /> Nuovo Cliente
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca per nome, cognome, email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 text-base"
          data-testid="loyalty-search"
        />
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">Nessun cliente trovato</p>
        ) : filtered.map((client) => {
          const points = client.loyalty_points || 0;
          const hasReward = points >= 10;
          return (
            <Card key={client.id} className={`card-elegant ${hasReward ? 'border-accent/50 bg-accent/5' : ''}`} data-testid={`loyalty-card-${client.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{client.first_name} {client.last_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{client.phone} · {client.email}</p>
                  </div>

                  {/* Stamp card */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                          i < points
                            ? 'bg-accent border-accent text-accent-foreground'
                            : 'border-muted-foreground/30 text-muted-foreground/30'
                        }`}
                      >
                        {i < points ? '✓' : (i + 1)}
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button
                      size="sm" variant="outline" className="h-8 w-8 p-0"
                      onClick={() => setRemovePointClient(client)}
                      disabled={points <= 0 || !!actionLoading}
                      data-testid={`loyalty-minus-${client.id}`}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="text-lg font-bold w-8 text-center" data-testid={`loyalty-points-${client.id}`}>{points}</span>
                    <Button
                      size="sm" className="h-8 w-8 p-0 bg-accent text-accent-foreground hover:bg-accent/90"
                      onClick={() => setAddPointClient(client)}
                      disabled={!!actionLoading}
                      data-testid={`loyalty-plus-${client.id}`}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>

                    {/* Edit points */}
                    <Button
                      size="sm" variant="outline" className="h-8 w-8 p-0"
                      onClick={() => { setEditClient(client); setEditPoints(points); }}
                      data-testid={`loyalty-edit-${client.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>

                    {hasReward && (
                      <Button
                        size="sm" className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white animate-pulse"
                        onClick={() => setRedeemClient(client)}
                        data-testid={`loyalty-redeem-${client.id}`}
                      >
                        <Gift className="h-3 w-3 mr-1" /> Omaggio
                      </Button>
                    )}

                    {/* Reset */}
                    <Button
                      size="sm" variant="outline" className="h-8 w-8 p-0 text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10 border-yellow-500/30"
                      onClick={() => setResetClient(client)}
                      disabled={points === 0}
                      data-testid={`loyalty-reset-${client.id}`}
                    >
                      <RotateCcw className="h-3 w-3" />
                    </Button>

                    {/* Delete */}
                    <Button
                      size="sm" variant="outline" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => setDeleteClient(client)}
                      data-testid={`loyalty-delete-${client.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {hasReward && (
                  <div className="mt-3 bg-accent/20 p-2.5 rounded-lg text-center">
                    <p className="text-sm font-semibold text-accent flex items-center justify-center gap-1">
                      <Gift className="h-4 w-4" /> TRATTAMENTO IN OMAGGIO!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* === CONFIRM +1 PUNTO === */}
      <AlertDialog open={!!addPointClient} onOpenChange={(open) => { if (!open) setAddPointClient(null); }}>
        <AlertDialogContent data-testid="confirm-add-point-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Aggiungere 1 punto?</AlertDialogTitle>
            <AlertDialogDescription>
              Confermi di voler aggiungere 1 punto fedeltà a <strong>{addPointClient?.first_name} {addPointClient?.last_name}</strong>?
              <br />Punti attuali: <strong>{addPointClient?.loyalty_points || 0}</strong> → <strong>{(addPointClient?.loyalty_points || 0) + 1}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleAddPoint} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="confirm-add-point-btn">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi Punto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* === CONFIRM -1 PUNTO === */}
      <AlertDialog open={!!removePointClient} onOpenChange={(open) => { if (!open) setRemovePointClient(null); }}>
        <AlertDialogContent data-testid="confirm-remove-point-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere 1 punto?</AlertDialogTitle>
            <AlertDialogDescription>
              Confermi di voler rimuovere 1 punto fedeltà a <strong>{removePointClient?.first_name} {removePointClient?.last_name}</strong>?
              <br />Punti attuali: <strong>{removePointClient?.loyalty_points || 0}</strong> → <strong>{Math.max(0, (removePointClient?.loyalty_points || 0) - 1)}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemovePoint} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-testid="confirm-remove-point-btn">
              <Minus className="h-4 w-4 mr-1" /> Rimuovi Punto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* === REDEEM DIALOG === */}
      <AlertDialog open={!!redeemClient} onOpenChange={(open) => { if (!open) setRedeemClient(null); }}>
        <AlertDialogContent data-testid="confirm-redeem-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Riscattare trattamento in omaggio?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{redeemClient?.first_name} {redeemClient?.last_name}</strong> ha raggiunto 10 punti. Confermi il trattamento in omaggio? I punti verranno azzerati automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-redeem">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleRedeem} className="bg-green-600 hover:bg-green-700" data-testid="confirm-redeem">
              <Gift className="h-4 w-4 mr-1" /> Riscatta Omaggio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* === RESET DIALOG === */}
      <AlertDialog open={!!resetClient} onOpenChange={(open) => { if (!open) setResetClient(null); }}>
        <AlertDialogContent data-testid="confirm-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Azzerare i punti?</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi azzerare i punti di <strong>{resetClient?.first_name} {resetClient?.last_name}</strong>? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-reset">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-reset">
              Azzera Punti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* === DELETE DIALOG === */}
      <AlertDialog open={!!deleteClient} onOpenChange={(open) => { if (!open) setDeleteClient(null); }}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare <strong>{deleteClient?.first_name} {deleteClient?.last_name}</strong>? Verranno eliminati anche tutte le sue prenotazioni e notifiche. Questa azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete">
              <Trash2 className="h-4 w-4 mr-1" /> Elimina Cliente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* === EDIT POINTS DIALOG === */}
      <Dialog open={!!editClient} onOpenChange={(open) => { if (!open) setEditClient(null); }}>
        <DialogContent className="max-w-sm" data-testid="edit-points-dialog">
          <DialogHeader>
            <DialogTitle>Modifica Punti</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Cliente: <strong>{editClient?.first_name} {editClient?.last_name}</strong>
            </p>
            <div>
              <Label>Punti Fedeltà</Label>
              <Input
                type="number" min="0" max="99"
                value={editPoints}
                onChange={(e) => setEditPoints(e.target.value)}
                data-testid="edit-points-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditClient(null)}>Annulla</Button>
            <Button onClick={handleEditPoints} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-points-btn">
              Salva Punti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === CREATE CLIENT DIALOG === */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-client-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input value={createForm.first_name} onChange={(e) => setCreateForm(p => ({ ...p, first_name: e.target.value }))} data-testid="create-first-name" />
              </div>
              <div>
                <Label>Cognome</Label>
                <Input value={createForm.last_name} onChange={(e) => setCreateForm(p => ({ ...p, last_name: e.target.value }))} data-testid="create-last-name" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm(p => ({ ...p, email: e.target.value }))} data-testid="create-email" />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={createForm.phone} onChange={(e) => setCreateForm(p => ({ ...p, phone: e.target.value }))} data-testid="create-phone" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={createForm.password} onChange={(e) => setCreateForm(p => ({ ...p, password: e.target.value }))} data-testid="create-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={createLoading} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="create-client-submit">
              {createLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
              Crea Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLoyaltySection;
